// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import XngUtilities from "@electrowire/App/Components/Common/XngUtilities";
import { xHeaderUpdate } from "@electrowire/App/Handlers/XNG/Header";
import useSize, { useElementSize } from "@electrowire/App/Hooks/useSize";
import { CustomAppState } from "@electrowire/App/Store/Reducers";
import { constants } from "@electrowire/XDL/constants";
import XngZone from "@gcg/App/Components/Common/XngZone";
import XngHeadInjector from "@gcg/App/Components/XngHeadInjector/XngHeadInjector";
import XngScriptInjector from "@gcg/App/Components/XngScriptInjector/XngScriptInjector";
import PopupContextProvider from "@gcg/App/Components/XPopup/XContext";
import { getDataLayer } from "@gcg/App/Utilities/DataLayer";
import { screen } from "@gcg/XDL/Helpers";
import { color, xScreen } from "@gcg/XDL/Utilities";
import { layoutContainer } from "@gcg/XDL/Utilities/sharedStyles";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import styled, { css, ThemeProps } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    // XNG-Changes
    registerMenu: state.xHeader.registerMenu,
    isSticky: state.xHeader.isSticky,
    currentLocation: getLocation(state),
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
    xHeaderUpdate,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: InjectableCss;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    // XNG-Changes
    secondaryNavigationContainer?: InjectableCss;
    secondaryNavigationWrapper?: InjectableCss;
    actions?: InjectableCss;
    mainWrapper?: InjectableCss;
    logoWrapper?: InjectableCss;
    mainNavigationWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
    mainMobileWrapper?: InjectableCss;
    mobileLeftWrapper?: InjectableCss;
    mobileCenterWrapper?: InjectableCss;
    mobileRightWrapper?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        css: css`
            width: 100%;
            position: fixed;
            z-index: ${getProp("theme.zIndex.header")};
            background-color: ${color("common.white")};
            box-shadow: 0 1px 0 0 ${color("common.ui7", 0.33)};
            top: 0;
            left: 0;
            right: 0;
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    secondaryNavigationContainer: {
        css: css`
            background-color: ${color("common.ui11")};
            box-shadow: 0 1px 0 0 ${color("common.ui3")};
        `,
    },
    secondaryNavigationWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            padding: 6px 0;
            ${layoutContainer}
        `,
    },
    actions: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 28px;
            padding-left: 28px;
            height: 30px;
            border-left: 1px solid ${color("common.ui3")};
            & > div:has(> div:empty) {
                display: none;
            }
        `,
    },
    mainWrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            //align-items: center;
            gap: 8px;
            & > div:last-child {
                margin-left: auto;
            }

            ${layoutContainer}
        `,
    },
    logoWrapper: {
        css: css`
            margin-right: 60px;
            flex-shrink: 0;
            flex-grow: 0;
            padding: 10px 0;
        `,
    },
    searchWrapper: {
        css: css`
            flex-shrink: 1;
            flex-grow: 1;
            width: 100%;
            max-width: 458px;
            min-width: 400px;
            display: flex;
            align-items: center;
            > div {
                width: 100%;
            }
        `,
    },
    mainNavigationWrapper: {
        css: css`
            flex-shrink: 1;
            flex-grow: 1;
            min-width: 200px;
        `,
    },
    mainMobileWrapper: {
        css: css`
            display: flex;
            align-items: center;
            column-gap: 8px;
            row-gap: 12px;
            padding: 8px 0;
            flex-wrap: wrap;
            justify-content: space-between;

            ${layoutContainer}
        `,
    },
    mobileLeftWrapper: {
        css: css`
            display: flex;
            order: 1;
            column-gap: 20px;
        `,
    },
    mobileCenterWrapper: {
        css: css`
            display: flex;
            order: 2;
            flex: 1 1 0;
            width: 100%;
            max-width: 352px;
            min-width: 330px;
            margin: auto;
            > div {
                width: 100%;
            }

            ${xScreen("lt", constants.header.breakpoints.sm)} {
                order: 3;
                max-width: 100%;
                min-width: 100%;
            }
        `,
    },
    mobileRightWrapper: {
        css: css`
            display: flex;
            column-gap: 28px;
            order: 3;
            ${xScreen("lt", constants.header.breakpoints.sm)} {
                order: 2;
                margin-top: 8px;
            }
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    registerMenu,
    currentLocation,
    isSticky,
    isTabletScreen,
    xHeaderUpdate,
}: Props) => {
    const headerRef = useRef<HTMLDivElement>(null);
    const headerWidth = typeof document !== "undefined" ? useElementSize(document.body)?.width : "100%";
    const headerHeight = useSize(headerRef)?.height;

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    
    // XNG-Changes: add height as CSS variable
    useEffect(() => {
        xHeaderUpdate({ key: "height", value: `${headerHeight}px` });
    }, [headerHeight]);

    // XNG-Changes: Hide main navigation
    useEffect(() => {
        setTimeout(() => hideAll());
    }, [isSticky, currentLocation]);
    
 


    const afterHeader = React.createRef<HTMLSpanElement>();

    // XNG-Changes: structure
    return (
        <PopupContextProvider>
            <StyledWrapper className="header-emptyDiv" style={{ height: headerHeight }}></StyledWrapper>
            <StyledWrapper as="header" ref={headerRef} {...styles.page} style={{ maxWidth: headerWidth }}>
                {/* {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>} */}
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    <XngZone contentId={id} zoneName="GlobalMessages" hint="Global Messages" />
                    {isTabletScreen && (
                        <StyledWrapper {...styles.mainMobileWrapper} data-test-selector="header-mobile">
                            <Hidden below="lg">
                                <XngZone
                                    contentId={id}
                                    zoneName="SecondaryNavigation"
                                    hint="SecondaryNavigation"
                                    normalWidth={true}
                                />
                            </Hidden>
                            <StyledWrapper {...styles.mobileLeftWrapper}>
                                <XngZone contentId={id} zoneName="MainNavigation" hint="MainNavigation" />
                                <XngZone contentId={id} zoneName="Logo" hint="Logo" normalWidth={true} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.mobileCenterWrapper}>
                                <XngZone contentId={id} zoneName="Search" hint="Search" normalWidth={true} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.mobileRightWrapper}>
                                <XngZone contentId={id} zoneName="MySalesRep" hint="My Sales" normalWidth={true} />
                                <XngZone contentId={id} zoneName="SignIn" hint="SignIn" normalWidth={true} />
                                <XngZone contentId={id} zoneName="Cart" hint="Cart" normalWidth={true} />
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    {!isTabletScreen && (
                        <>
                            <StyledWrapper
                                {...styles.secondaryNavigationContainer}
                                data-test-selector="header-secondary-navigation"
                            >
                                <StyledWrapper {...styles.secondaryNavigationWrapper}>
                                    <XngZone
                                        contentId={id}
                                        zoneName="SecondaryNavigation"
                                        hint="SecondaryNavigation"
                                        normalWidth={true}
                                    />
                                    <StyledWrapper {...styles.actions}>
                                        <XngZone
                                            contentId={id}
                                            zoneName="MySalesRep"
                                            hint="My Sales"
                                            normalWidth={true}
                                        />
                                        <XngZone contentId={id} zoneName="SignIn" hint="SignIn" normalWidth={true} />
                                        <XngZone contentId={id} zoneName="Cart" hint="Cart" normalWidth={true} />
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.mainWrapper}>
                                <StyledWrapper {...styles.logoWrapper}>
                                    <XngZone contentId={id} zoneName="Logo" hint="Logo" normalWidth={true} />
                                </StyledWrapper>
                                <StyledWrapper {...styles.mainNavigationWrapper}>
                                    <XngZone contentId={id} zoneName="MainNavigation" hint="MainNavigation" />
                                </StyledWrapper>
                                <StyledWrapper {...styles.searchWrapper}>
                                    <XngZone contentId={id} zoneName="Search" hint="Search" normalWidth={true} />
                                </StyledWrapper>
                            </StyledWrapper>
                        </>
                    )}
                    <XngZone contentId={id} zoneName="AdditionalScripts" hint="Additional Scripts" />
                    <XngZone contentId={id} zoneName="Breadcrumb" hint="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
                <XngScriptInjector />
                <XngHeadInjector />
                <XngUtilities />
            </StyledWrapper>
            <Transition
                mountOnEnter
                unmountOnExit
                in={registerMenu && registerMenu.length > 0}
                timeout={{ enter: 75, exit: 250 }}
            >
                {state => (
                    <StyledOverlay
                        className={registerMenu && isTabletScreen ? "header-overlay" : ""}
                        transitionState={state}
                    />
                )}
            </Transition>
            </PopupContextProvider>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;

const StyledOverlay = styled.div<{ transitionState: TransitionStatus } & ThemeProps<BaseTheme>>`
    position: fixed;
    right: 0;
    bottom: 0;
    top: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: ${getProp("theme.zIndex.styledOverlay")};
    transition: opacity ease-in-out 250ms;
    ${({ transitionState }) => {
        switch (transitionState) {
            case "entering":
                return "opacity: 0;";
            case "entered":
                return "opacity: 1;";
            case "exiting":
            case "exited":
                return "opacity: 0;";
            default:
                return "";
        }
    }}
`;
